import React, { memo, useCallback, useMemo, useRef } from "react";
import "./index.scss";

type RangeSliderProps = {
  min: number;
  max: number;
  value: number;
  step: number;
  onChange: (newValue: number) => void;
};

const RangeSlider: React.FC<RangeSliderProps> = ({ min = 1, max = 10, value, step = 1, onChange }) => {
  const controlsRef = useRef<HTMLDivElement | null>(null);

  const clampAndStep = useCallback(
    (rawValue: number) => {
      const clampedValue = Math.min(Math.max(rawValue, min), max);
      return Math.round(clampedValue / step) * step;
    },
    [min, max, step],
  );

  const getPercent = useCallback((val: number) => ((val - min) / (max - min)) * 100, [min, max]);

  function updateValue(clientX: number) {
    if (!controlsRef.current) return;

    const { left = 0, width = 100 } = controlsRef.current.getBoundingClientRect();
    let newPercent = (clientX - left) / width;
    newPercent = Math.min(Math.max(newPercent, 0), 1);

    const rawValue = min + newPercent * (max - min);
    const newValue = clampAndStep(rawValue);

    console.log("values: ", value, newValue);
    if (newValue !== value) {
      onChange(newValue);
    }
  }

  const onDragStart = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === "mousedown") {
      document.addEventListener("mousemove", onDragMove);
      document.addEventListener("mouseup", onDragEnd);
    } else if (e.type === "touchstart") {
      document.addEventListener("touchmove", onDragMove);
      document.addEventListener("touchend", onDragEnd);
    }
  };

  const onDragMove = (e: any) => {
    e.preventDefault();

    let clientX;
    if (e.type === "mousemove") {
      clientX = e.clientX;
    } else if (e.type === "touchmove") {
      clientX = e.touches[0].clientX;
    }
    console.log("clientX: ", clientX);
    console.log("controlsRef.current", controlsRef.current);
    if (controlsRef.current && clientX !== undefined) {
      const { left, width } = controlsRef.current.getBoundingClientRect();
      let newPercent = (clientX - left) / width;
      newPercent = Math.min(Math.max(newPercent, 0), 1);

      const rawValue = min + newPercent * (max - min);
      const newValue = clampAndStep(rawValue);

      if (newValue !== value) {
        onChange(newValue);
      }
    }
  };

  const onDragEnd = (e: any) => {
    e.preventDefault();

    if (e.type === "mouseup") {
      document.removeEventListener("mousemove", onDragMove);
      document.removeEventListener("mouseup", onDragEnd);
    } else if (e.type === "touchend") {
      document.removeEventListener("touchmove", onDragMove);
      document.removeEventListener("touchend", onDragEnd);
    }
  };

  const onTrackClick = (e: any) => {
    if (e.target instanceof HTMLElement && e.target.getAttribute("aria-label") === "slider-thumb") {
      return;
    }
    updateValue(e?.clientX ?? e.changedTouches?.[0]?.clientX);
  };

  const fillWidth = value === 0 ? "0%" : `${getPercent(value)}%`;
  const totalMarks = max - min + 1;
  const marks = useMemo(() => Array.from({ length: totalMarks }, (_, i) => min + i), [totalMarks, min]);

  const marksElements = marks.map((mark) => {
    if (mark > max) return null;

    const markPercent = getPercent(mark);
    const isActive = value !== 0 && mark <= value;
    const isSelected = value !== 0 && mark === value;

    return (
      <div
        key={mark}
        className={`range-slider__mark ${isActive ? "range-slider__mark--active" : ""} ${
          isSelected ? "range-slider__mark--selected" : ""
        }`}
        style={{ left: `${markPercent}%` }}
      />
    );
  });

  return (
    <div className="range-slider" aria-label="range-slider-component">
      {value !== 0 && (
        <div className="range-slider__value-banner" style={{ left: fillWidth }}>
          {value}
        </div>
      )}
      <div
        className="range-slider__controls"
        ref={controlsRef}
        aria-label="slider-controls"
        onClick={onTrackClick}
        onTouchEnd={onTrackClick}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        <div className="range-slider__track" aria-label="slider-track" aria-hidden="true" />
        <div className="range-slider__fill" aria-label="slider-fill" style={{ width: fillWidth }} />
        {marksElements}
      </div>
    </div>
  );
};

export default memo(RangeSlider);
